import axios from 'axios';

const BASE = '/api/services/reservare-be/api/app/waiting-lists';
const auth = { withCredentials: true, headers: { 'X-Auth': 'Auth0' } };

export type WaitingListStatus = 'ACTIVE' | 'CONTACTED' | 'BOOKED' | 'EXPIRED' | 'CANCELLED' | 'NO_RESPONSE';

export interface WaitingListItem {
  id: string;
  status: WaitingListStatus;
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;
  /** "HH:mm:ss" — pole vrací Spring Boot Jackson z LocalTime. */
  timeFrom?: string;
  timeTo?: string;
  notes?: string;
  contactedAt?: string;
  contactNotes?: string;
  priority: number;
  createdAt: string;
  updatedAt: string;
  customer: { id: string; firstName: string; lastName: string; email: string; phone: string };
  service: { id: string; name: string };
  salon: { id: string; name: string };
  preferredWorker?: { id: string; displayName: string };
  resultingBookingId?: string;
}

export interface WaitingListPage {
  content: WaitingListItem[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface CreateWaitingListBody {
  customerId: string;
  serviceId: string;
  dateFrom: string;
  dateTo: string;
  timeFrom?: string; // "HH:mm"
  timeTo?: string;
  notes?: string;
  priority?: number;
  preferredWorkerId?: string;
}

export type UpdateWaitingListBody = Partial<CreateWaitingListBody> & { status?: WaitingListStatus };

export async function fetchWaitingList(
  salonId: string,
  params: { status?: WaitingListStatus; page?: number; size?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' } = {}
): Promise<WaitingListPage> {
  const res = await axios.get<WaitingListPage>(`${BASE}/salons/${salonId}`, {
    ...auth,
    params: { page: 0, size: 50, ...params },
  });
  return res.data;
}

export async function createWaitingList(salonId: string, body: CreateWaitingListBody): Promise<WaitingListItem> {
  const res = await axios.post<WaitingListItem>(`${BASE}/salons/${salonId}`, body, auth);
  return res.data;
}

export async function updateWaitingList(
  salonId: string,
  id: string,
  body: UpdateWaitingListBody
): Promise<WaitingListItem> {
  const res = await axios.put<WaitingListItem>(`${BASE}/${id}/salons/${salonId}`, body, auth);
  return res.data;
}

export async function deleteWaitingList(salonId: string, id: string): Promise<void> {
  await axios.delete(`${BASE}/${id}/salons/${salonId}`, auth);
}

export async function markContacted(
  salonId: string,
  id: string,
  contactNotes: string
): Promise<WaitingListItem> {
  const res = await axios.post<WaitingListItem>(
    `${BASE}/${id}/salons/${salonId}/contact`,
    { contactNotes },
    auth
  );
  return res.data;
}

export async function markBooked(
  salonId: string,
  id: string,
  bookingId: string
): Promise<WaitingListItem> {
  const res = await axios.post<WaitingListItem>(
    `${BASE}/${id}/salons/${salonId}/book?bookingId=${bookingId}`,
    null,
    auth
  );
  return res.data;
}

/** Helper — odřízne ":ss" z "HH:mm:ss" stringu vráceného BE. */
export const trimTime = (t?: string): string => (t ? t.substring(0, 5) : '');

/** Vyřeší zda waiting entry reprezentuje konkrétní termín (jeden den, jeden čas). */
export function isExactSlot(item: { dateFrom: string; dateTo: string; timeFrom?: string; timeTo?: string }): boolean {
  return item.dateFrom === item.dateTo && !!item.timeFrom && item.timeFrom === item.timeTo;
}
